import React, { useEffect, useState } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormHelperText,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';

const NEW_CATEGORY = '__new_category__';

/**
 * Category select: Uncategorized, existing names, or New Category… (opens a dialog).
 */
const PartCategoryField = ({
  value = '',
  onChange,
  options = [],
  disabled = false,
  fullWidth = true,
  size = 'small',
  label = 'Category',
  helperText = 'Optional'
}) => {
  const normalizedValue = value != null ? String(value).trim() : '';
  const known = Array.isArray(options) ? options.filter(Boolean) : [];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [selectKey, setSelectKey] = useState(0);

  // If value is a brand-new name not yet in options, still show it in the closed select.
  const displayOptions =
    normalizedValue && !known.some((o) => o.toLowerCase() === normalizedValue.toLowerCase())
      ? [...known, normalizedValue]
      : known;

  useEffect(() => {
    if (!dialogOpen) setDraftName('');
  }, [dialogOpen]);

  const handleSelect = (e) => {
    const next = e.target.value;
    if (next === NEW_CATEGORY) {
      setDraftName('');
      setDialogOpen(true);
      // Force Select to re-render on prior value (MenuItem click would otherwise stick on sentinel).
      setSelectKey((k) => k + 1);
      return;
    }
    onChange?.(next);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setDraftName('');
  };

  const handleDialogSave = () => {
    const name = draftName.trim().slice(0, 50);
    if (!name) return;
    onChange?.(name);
    setDialogOpen(false);
    setDraftName('');
  };

  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
      <FormControl fullWidth={fullWidth} size={size} disabled={disabled}>
        <InputLabel id="part-category-select-label" shrink>
          {label}
        </InputLabel>
        <Select
          key={selectKey}
          labelId="part-category-select-label"
          label={label}
          notched
          value={normalizedValue}
          onChange={handleSelect}
          displayEmpty
          renderValue={(selected) => {
            if (selected === '' || selected == null) return 'Uncategorized';
            return selected;
          }}
        >
          <MenuItem value="">
            <em>Uncategorized</em>
          </MenuItem>
          {displayOptions.map((cat) => (
            <MenuItem key={cat} value={cat}>
              {cat}
            </MenuItem>
          ))}
          <MenuItem value={NEW_CATEGORY}>New Category…</MenuItem>
        </Select>
        {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
      </FormControl>

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle>New Category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Category name"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleDialogSave();
              }
            }}
            inputProps={{ maxLength: 50 }}
            sx={{ mt: 0.5 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleDialogSave}
            disabled={!draftName.trim()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartCategoryField;
